import { Box, Stack } from "@mui/material";
import { type SupportArticle } from "@src/appV2/HelpCenter/types";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";

import { Icon } from "../../components/Icon";
import { Text } from "../../components/Text";
import { applyPlatformAwareInteractionStyles } from "../../utils/applyPlatformAwareInteractionStyles";
import { getBackgroundMuted, getBorderBase, getMutedText, getStrongText } from "../themeHelpers";

interface SupportArticleCardProps {
  supportArticle: SupportArticle;
}

export function SupportArticleCard(props: SupportArticleCardProps) {
  const { supportArticle } = props;

  return (
    <Box
      component="a"
      href={supportArticle.attributes.distributionUrl}
      rel="noopener noreferrer"
      target="_blank"
      sx={(theme) => ({
        display: "block",
        width: "100%",
        textDecoration: "none",
        borderRadius: theme.borderRadius?.large,
        border: `1px solid ${getBorderBase(theme)}`,
        backgroundColor: theme.palette.background.paper,
        paddingTop: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        boxShadow: theme.shadow?.card,
        transition: theme.transitions.create(
          ["border-color", "transform", "box-shadow", "background-color"],
          { duration: theme.transitions.duration.shorter }
        ),
        ...applyPlatformAwareInteractionStyles({
          hoverStyle: {
            backgroundColor: getBackgroundMuted(theme),
            transform: "translateY(-1px)",
            boxShadow: `0 0.75rem 1.75rem rgba(0,0,0,0.07)`,
          },
        }),
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: "2px",
        },
      })}
      onClick={() => {
        logEvent(APP_V2_USER_EVENTS.HELP_CENTER_ARTICLE_OPENED, {
          id: supportArticle.id,
          title: supportArticle.attributes.title,
          category: supportArticle.attributes.category,
        });
      }}
    >
      <Stack alignItems="center" direction="row" spacing={2}>
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "2.5rem",
            height: "2.5rem",
            minWidth: "2.5rem",
            borderRadius: theme.borderRadius?.large,
            backgroundColor: theme.palette.background.warning,
          })}
        >
          <Icon
            size="medium"
            sx={(theme) => ({ color: theme.palette.primary.main })}
            type="documents"
          />
        </Box>

        <Stack sx={{ flex: 1, minWidth: 0, gap: "0.25rem" }}>
          <Text
            semibold
            variant="body2"
            sx={(theme) => ({
              color: getStrongText(theme),
            })}
          >
            {supportArticle.attributes.title}
          </Text>
          <Text variant="caption" sx={(theme) => ({ color: getMutedText(theme) })}>
            {supportArticle.attributes.category}
          </Text>
        </Stack>

        <Icon size="small" sx={(theme) => ({ color: getMutedText(theme) })} type="chevron-right" />
      </Stack>
    </Box>
  );
}
